import {
  MANUAL_ADDRESS_STREET_CHAR_LIMIT,
  MANUAL_ADDRESS_SUBURB_CHAR_LIMIT,
} from '~/constants/domain-specific';

import { useAppContent } from './useAppContent';

/**
 * Hook that provides localized field overrides configuration for AddressSearch component.
 * Encapsulates all field labels and error messages with dynamic character limit tokens.
 *
 * @returns Field overrides object ready to pass to AddressSearch component
 *
 * @example
 * ```tsx
 * const fieldOverrides = useAddressFieldOverrides();
 * <AddressSearch fieldOverrides={fieldOverrides} ... />
 * ```
 */
export const useAddressFieldOverrides = () => {
  const { getTextContent } = useAppContent({
    basePath: 'dsme.addressComponent',
    state: {
      numberStreetInputCharLimit: MANUAL_ADDRESS_STREET_CHAR_LIMIT,
      suburbInputCharLimit: MANUAL_ADDRESS_SUBURB_CHAR_LIMIT,
    },
    tokens: {
      numberStreetInputCharLimit: (state) => state.numberStreetInputCharLimit,
      suburbInputCharLimit: (state) => state.suburbInputCharLimit,
    },
  });

  // Lookup entry field labels and helper text
  const lookupLabel = getTextContent('fields.lookupInput.label');
  const includeUnitNumberText = getTextContent('fields.lookupInput.includeUnitNumber');
  const noResultsText = getTextContent('fields.lookupInput.noResults');
  const cantFindText = getTextContent('fields.lookupInput.cantFindTheAddress');
  const enterManuallyText = getTextContent('fields.lookupInput.enterItManually');
  const returnToLookupText = getTextContent('fields.lookupInput.returnToLookup');

  // Manual entry field labels
  const streetAddressLabel = getTextContent('fields.manualNumberStreetInput.label');
  const suburbLabel = getTextContent('fields.manualSuburbInput.label');
  const stateLabel = getTextContent('fields.manualStateInput.label');
  const postcodeLabel = getTextContent('fields.manualPostcodeInput.label');

  // Error messages
  const streetAddressError = getTextContent('fields.manualNumberStreetInput.invalidText');
  const suburbError = getTextContent('fields.manualSuburbInput.invalidText');
  const stateError = getTextContent('fields.manualStateInput.invalidText');
  const postcodeError = getTextContent('fields.manualPostcodeInput.invalidText');

  const streetSpecialCharError = getTextContent(
    'fields.manualNumberStreetInput.specialCharacterError'
  );
  const suburbSpecialCharError = getTextContent('fields.manualSuburbInput.specialCharacterError');

  // Return the field overrides object to be used in AddressSearch component
  const fieldOverrides = {
    lookup: {
      label: lookupLabel,
      helperText: includeUnitNumberText,
      noResults: noResultsText,
      cantFindText: cantFindText,
      enterManuallyText: enterManuallyText,
      returnToLookupText: returnToLookupText,
    },
    fields: {
      streetAddress1: {
        label: streetAddressLabel,
        errorMessage: (err: string) => {
          if (err === 'invalid_characters') return streetSpecialCharError;

          return streetAddressError;
        },
        displayIcon: true,
        maxLength: 40,
      },
      city: {
        label: suburbLabel,
        errorMessage: (err: string) => {
          if (err === 'invalid_characters') return suburbSpecialCharError;

          return suburbError;
        },
        displayIcon: true,
        maxLength: 25,
      },
      state: {
        label: stateLabel,
        errorMessage: stateError,
      },
      postcode: {
        label: postcodeLabel,
        errorMessage: postcodeError,
        displayIcon: true,
        maxLength: 4,
      },
    },
  };

  return fieldOverrides;
};
